import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { useDisclosure } from '@shared/hooks';
import { useEffect, useState } from 'react';
import {
  generatePath,
  matchPath,
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from 'react-router-dom';

import {
  AddOutlinedIcon,
  Button,
  DesignServicesOutlinedIcon,
  ExamExitModal,
  IconButton,
  LanguageSwitchMenu,
  RemoveOutlinedIcon,
  Tooltip,
} from '../../../components';
import { NetworkAlert } from '../../../containers';
import { useExamEvent, useNewExam, useSnackbar } from '../../../providers';
import { PATHS } from '../../constants';
import { ExamTools, TimerAlert } from '../../exam/containers';
import { useFontSize } from '../../exam/contexts/FontSizeContext';
import ExamFooter from '../../exam/layouts/ExamFooter';
import { ExamPreviewLoading } from '../completed/components';
import { ExamEventPreviewBanner } from '../components';
import { ExamPreviewHeader } from '../containers';

function ExamPreviewLayoutContent({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams();

  const { t, i18n } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const { language, changeLanguage } = i18n;
  const [isExitModalOpen, { close: closeExitModal }] = useDisclosure(false);
  const [searchParams] = useSearchParams({});
  const { questions, currentExamOrder, examTime, loading, error } = useNewExam();
  const { examEvent } = useExamEvent();
  const [showExamTools, setShowExamTools] = useState(true);
  const q = searchParams.get('q') ?? '';
  const page = searchParams.get('page') ?? '';
  const isExamRoomPage = page !== 'review' && q !== '';
  const isReviewPage = page === 'review';
  const isCompletedPage = matchPath(PATHS.Preview.Completed, location.pathname);

  const { increaseFontSize, decreaseFontSize, fontScale } = useFontSize();

  const documentURL: string = examEvent.exam?.metadata?.documentURL;

  const shouldHideTimer = !examTime?.endedAt || isCompletedPage;

  useEffect(() => {
    if (error) {
      showSnackbar({
        variant: 'error',
        message: t('examPreview.fetchExamError'),
      });
      navigate(generatePath(PATHS.Preview.Instruction, { taskId }));
    }
  }, [error, showSnackbar, t, navigate, taskId]);

  const menus: JSX.Element[] = [
    // Font Size Adjustment
    <div key='font-size-adjust' className='flex items-center gap-1'>
      <Tooltip content={t('header.fontSizeDecrease')} position='bl' className='z-50 font-semibold'>
        <IconButton onClick={decreaseFontSize} disabled={fontScale === -1}>
          <RemoveOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip content={t('header.fontSize')} position='bl' className='z-50 font-semibold'>
        <div className='text-body1 bg-grey-100 grid size-10 place-items-center rounded-sm px-3 py-2 font-semibold'>
          A
        </div>
      </Tooltip>
      <Tooltip content={t('header.fontSizeIncrease')} position='bl' className='z-50 font-semibold'>
        <IconButton onClick={increaseFontSize} disabled={fontScale === 1}>
          <AddOutlinedIcon />
        </IconButton>
      </Tooltip>
      <LanguageSwitchMenu
        key='language-switch-menu'
        currentLanguage={language.toUpperCase()}
        onSwitchToEnglish={() => {
          changeLanguage('en');
        }}
        onSwitchToThai={() => {
          changeLanguage('th');
        }}
      />
    </div>,

    ...(isExamRoomPage
      ? [
          <Button
            key='exam-tools-button'
            variant='outlined'
            className={cn(
              'px-3 py-2',
              showExamTools && 'bg-warning-100 border-warning-700 text-warning-700',
            )}
            onClick={() => setShowExamTools(!showExamTools)}
          >
            <div className='flex items-center gap-2 font-semibold'>
              <DesignServicesOutlinedIcon
                className={cn('text-xl', showExamTools && 'text-warning')}
              />
              {t('header.examTools')}
            </div>
          </Button>,
        ]
      : []),
  ];
  if (loading) {
    return <ExamPreviewLoading variant='exam' />;
  }
  return (
    <>
      <ExamExitModal
        open={isExitModalOpen}
        title='Do You Want To Exit the Exam?'
        description={`Testing time will keep counting down while you're away.`}
        disabledCheckBox={false}
        onClose={closeExitModal}
        onExit={() => navigate(PATHS.Home)}
        exitText='Exit'
      />
      <ExamEventPreviewBanner />
      <ExamPreviewHeader menus={menus} withTimer={!shouldHideTimer} />
      <div className='bg-grey-50 flex flex-1 flex-col overflow-auto p-6'>
        {shouldHideTimer ? null : <TimerAlert />}
        <NetworkAlert />
        <div
          className={cn(
            'flex flex-1 flex-row',
            showExamTools && isExamRoomPage && 'gap-6',
            isReviewPage && 'gap-6',
          )}
        >
          <div
            className={cn(
              'transition-all duration-300',
              showExamTools && isExamRoomPage ? 'w-[calc(100%-364px)]' : 'w-full',
            )}
          >
            {children}
          </div>
          {isExamRoomPage ? (
            <div className='flex-1'>
              <ExamTools
                questions={questions ?? []}
                currentExamOrder={currentExamOrder}
                show={showExamTools}
                onClose={() => setShowExamTools(false)}
                url={documentURL}
              />
            </div>
          ) : null}
        </div>
      </div>
      {isExamRoomPage ? <ExamFooter /> : null}
    </>
  );
}

export default function ExamPreviewLayout(props: { children: React.ReactNode }) {
  return <ExamPreviewLayoutContent {...props} />;
}
